/* =============================================================================
 * analytics.js — PostHog + first-touch attribution for interview.clozo.ai
 *
 * WHY THIS IS AN EXTERNAL FILE AND NOT AN INLINE <script>:
 * site/pricing.html carries its own stricter Content-Security-Policy in a
 * <meta http-equiv> tag whose script-src permits inline code only by sha256
 * hash. An inline block here would be silently blocked on the single most
 * important page on the site — no error the visitor sees, just no events. A
 * file under /assets/ is covered by 'self' in both policies and needs no hash.
 *
 * EDIT POINT: this file is referenced from site/components/navbar.html ONLY.
 * Run site/bake-components.sh to publish the tag to every page. Never add the
 * <script> tag to an individual page by hand — the bake overwrites it.
 *
 * CONSENT GATING (added 2026-09-11): PostHog is a non-essential analytics
 * cookie, so it must NOT load until the visitor accepts in the consent banner
 * (assets/js/consent.js). This file therefore no longer inits PostHog on load;
 * it EXPOSES window.__clozoInitAnalytics(), which consent.js calls once, only
 * after consent is granted (or already stored granted, and never on a sensitive
 * page — login/checkout/etc). The first-touch attribution below is first-party
 * localStorage only (it makes no network request), so it keeps running always;
 * clozo.track() is a no-op until PostHog is actually initialised.
 *
 * The six event names are the whole contract (GTM plan, Day 1 Hour 1):
 *   $pageview | signup | download_click | activation | checkout_start | purchase
 * ============================================================================= */
(function () {
  'use strict';

  var POSTHOG_KEY  = 'phc_meqzwzkgbbo3wUewcnJjh69dHtiouHvZamq2VdseSYVC';
  var POSTHOG_HOST = 'https://us.i.posthog.com';

  /* --- 1. Consent-gated PostHog init --------------------------------------- */
  /* Idempotent: consent.js may call it more than once (accept, then a later
     re-open); the __SV guard inside the loader and this flag both make the
     second call a no-op. Never throws — analytics must not break a page. */
  var started = false;
  window.__clozoInitAnalytics = function () {
    if (started) return;
    started = true;
    try {
      /* posthog-js loader snippet (loads array.js from us-assets) */
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: true
      });
    } catch (e) { /* analytics must never break a page */ }
  };

  /* --- 2. window.clozo — first-touch attribution ---------------------------- */
  var STORE_KEY  = 'clozo_attr';
  var UTM_KEYS   = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  /* Ad-platform CLICK IDENTIFIERS. Each network appends its own when it sends a
     click, and it is the ONLY thing that ties a later purchase back to the exact
     ad. checkout.js forwards these into the Stripe Checkout Session metadata, so
     the sale carries its click id — which is what server-side conversion uploads
     need, and what makes a dispute defensible. First touch wins, like the UTMs. */
  var CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'rdt_cid', 'li_fat_id', 'epik'];
  var OWN_HOST   = 'interview.clozo.ai';

  // Safari private mode throws on any localStorage access, so every read and
  // every write below is wrapped. A storage failure must degrade attribution,
  // never break the page.
  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) { return null; }
  }

  function writeStore(obj) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(obj)); return true; }
    catch (e) { return false; }
  }

  var clozo = {
    /**
     * utm() — record the visitor's FIRST touch. Runs once per page load.
     * First touch wins: if clozo_attr already exists it is never overwritten.
     * The affiliate code is NOT stored here: referral.js owns it (last click, 90 days).
     */
    utm: function () {
      try {
        if (readStore()) return;                       // first touch already recorded

        var params = new URLSearchParams(window.location.search);
        var attr = {};
        var found = false;

        for (var i = 0; i < UTM_KEYS.length; i++) {
          var v = params.get(UTM_KEYS[i]);
          if (v) { attr[UTM_KEYS[i]] = v; found = true; }
        }

        if (!found) return;                            // nothing to attribute

        attr.first_seen_at = new Date().toISOString();
        attr.landing_path  = window.location.pathname;

        // Referrer, but only when it is not our own site (an internal click is
        // not a source).
        try {
          var ref = document.referrer;
          if (ref) {
            var host = new URL(ref).hostname;
            if (host && host !== OWN_HOST) attr.referrer = ref;
          }
        } catch (e) { /* malformed referrer — skip it, keep the rest */ }

        writeStore(attr);
      } catch (e) { /* never throw out of analytics */ }
    },

    /** attr() — the stored first-touch object, or {}. Never throws. */
    attr: function () {
      return readStore() || {};
    },

    /**
     * forgetClickIds() — erase every advertising identifier from the stored
     * record, keeping the first-party utm/via attribution. Called by consent.js
     * when advertising consent is withdrawn: stopping the pixels is not enough
     * if the click id stays on disk and is attached to a later purchase.
     */
    forgetClickIds: function () {
      try {
        var attr = readStore();
        if (!attr) return;
        var changed = false;
        var drop = CLICK_KEYS.concat(['fbc', 'click_seen_at']);
        for (var i = 0; i < drop.length; i++) {
          if (attr[drop[i]] !== undefined) { delete attr[drop[i]]; changed = true; }
        }
        if (changed) writeStore(attr);
      } catch (e) { /* never throw out of analytics */ }
    },

    /**
     * captureClickIds() — merge advertising click identifiers from the CURRENT
     * url into the first-touch record.
     *
     * CONSENT-GATED ON PURPOSE: consent.js calls this from loadTrackers(), i.e.
     * only once the visitor has accepted. A gclid/fbclid is an ad-platform
     * identifier, not first-party analytics, so storing one before the banner is
     * answered (or after "Reject all", or against a GPC signal) would contradict
     * our own Cookie Policy. Waiting costs nothing: the landing URL is still the
     * current URL when consent is given. Safe to call repeatedly.
     */
    captureClickIds: function () {
      try {
        var params = new URLSearchParams(window.location.search);
        var attr = readStore() || {};
        var found = false;
        for (var i = 0; i < CLICK_KEYS.length; i++) {
          var v = params.get(CLICK_KEYS[i]);
          if (!v) continue;
          /* LAST touch, deliberately — the opposite rule to utm/via above. Ad
             platforms credit the MOST RECENT click, and Google Ads rejects a
             gclid older than 90 days outright, so a first-touch click id would
             either be refused on upload or credit the wrong campaign. The
             partner 'via' and the utm set stay first-touch, because affiliate
             payout depends on who introduced the customer. */
          attr[CLICK_KEYS[i]] = v;
          found = true;
        }
        if (!found) return;
        var nowMs = Date.now();
        // The click TIME is not recoverable later and is required downstream:
        // it dates the click for the 90-day freshness check in checkout.js, and
        // Meta's Conversions API wants fbc ("fb.1.<click ms>.<fbclid>"), not a
        // bare fbclid — a bare one will not match a conversion.
        attr.click_seen_at = new Date(nowMs).toISOString();
        if (attr.fbclid) attr.fbc = 'fb.1.' + nowMs + '.' + attr.fbclid;
        if (!attr.first_seen_at) attr.first_seen_at = new Date(nowMs).toISOString();
        if (!attr.landing_path) attr.landing_path = window.location.pathname;
        writeStore(attr);
      } catch (e) { /* never throw out of analytics */ }
    },

    /**
     * track(name, props) — send a named event carrying the visitor's original
     * UTM/partner set. Guarded on window.posthog: an ad blocker or a CSP block
     * must not throw inside a click handler, because a thrown error there
     * kills the click itself.
     */
    track: function (name, props) {
      // Clarity label leg (W6, OBSERVABILITY H-8): content-free smart-event label for EVERY visitor, independent of
      // PostHog consent (Clarity itself runs for everyone, D12.10). site-runtime.js keeps the fixed label list
      // (signup, download_click, activation, checkout_start, purchase, book_view, book_cta, …) and ignores the rest.
      try {
        var rt = window.__clozoRuntime;
        if (rt && typeof rt.clarityEvent === 'function') rt.clarityEvent(name);
      } catch (e) { /* never throw out of a click handler */ }
      // PostHog leg: guarded on window.posthog, which is undefined until consent
      // is granted (consent.js -> __clozoInitAnalytics), so this no-ops before
      // consent instead of queueing.
      try {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          var base = clozo.attr();
          var payload = {};
          for (var k in base)  { if (Object.prototype.hasOwnProperty.call(base, k))  payload[k] = base[k]; }
          for (var j in props) { if (Object.prototype.hasOwnProperty.call(props, j)) payload[j] = props[j]; }
          window.posthog.capture(name, payload);
        }
      } catch (e) { /* swallow */ }
      // Advertising-pixel leg: __clozoPixels exists only after consent + init, so
      // this too is a no-op before consent. Separate try/catch so a pixel error
      // can never stop the PostHog capture above, or vice versa.
      try {
        if (window.__clozoPixels && typeof window.__clozoPixels.fire === 'function') {
          window.__clozoPixels.fire(name, props || {});
        }
      } catch (e) { /* never throw out of a click handler */ }
    }
  };

  window.clozo = clozo;

  /* --- 3. Record the first touch, once, right after init ------------------- */
  clozo.utm();
})();
